import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { BUTTON_FIELD_CLASSES } from "@shared/utils/constants";
import { FilePlus, Info, Check } from "lucide-react";
import { useChoices, useCreateSlipLot } from "../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { toast } from "@shared/hooks/use-toast";

interface CreateSlipLotDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateSlipLotDialog: React.FC<CreateSlipLotDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const { mutate: createSlipLot, isPending } = useCreateSlipLot();

  const fields: FormField[] = [
    {
      name: "paymentCentre",
      label: "Payment Centre",
      type: "select",
      options: [
        {
          label: "DTW - Downtown Payment Centre",
          value: "DTW",
        },
        { label: "WST - Westside Outlet", value: "WST" },

        {
          label: "APT - Airport Terminal Kiosk",
          value: "APT",
        },
        {
          label: "CTH - City Hall Counter",
          value: "CTH",
        },
        {
          label: "MOL - Mall Outlet — Park Ave",
          value: "MOL",
        },
        {
          label: "GRC - Grocery Chain Partner",
          value: "GRC",
        },
      ],
      required: true,
      className: "col-span-1",
    },
    {
      name: "collectionDate",
      label: "Collection Date",
      type: "datepicker",
      required: true,
      className: "col-span-1",
    },
    {
      name: "physicalSlipCount",
      label: "Physical Slip Count",
      type: "number",
      placeholder: "e.g. 500",
      helperText: "From the handover sheet / bundle count.",
      required: true,
      min: 1,
      className: "col-span-1",
    },
    {
      name: "initialEntryMode",
      label: "Initial Entry Mode",
      type: "select",
      options: [
        {
          label: "Manual Entry",
          value: "Manual Entry",
        },
        {
          label: "Barcode scan",
          value: "Barcode scan",
        },
      ],
      defaultValue: "Manual Entry",
      className: "col-span-1",
    },
    {
      name: "handoverNotes",
      label: "Handover Notes",
      type: "textarea",
      placeholder: "Optional — courier ref, any remarks",
      className: "col-span-2",
    },
  ];

  const form = FormService.createForm({
    fields: fields,
    defaultValues: {
      paymentCentre: "APT",
      collectionDate: new Date(),
      physicalSlipCount: 1,
      initialEntryMode: "Manual Entry",
      handoverNotes: "",
    },
  });

  const handleSubmit = (data: any) => {
    if (!data.collectionDate) {
      toast({
        variant: "destructive",
        description: "Select a collection date.",
      });
      return;
    }

    if (!data.physicalSlipCount || Number(data.physicalSlipCount) <= 0) {
      toast({
        variant: "destructive",
        description: "Enter a valid physical slip count.",
      });
      return;
    }
    const payload = {
      centre_code: data.paymentCentre,
      collection_date: data.collectionDate,
      physical_slip_count: Number(data.physicalSlipCount),
      entry_mode: data.initialEntryMode,
      handover_notes: data.handoverNotes,
      remote_utility_id: Number(remoteUtilityId),
    };

    createSlipLot(payload, {
      onSuccess: () => {
        onClose();
        form.reset();
      },
    });
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent className="sm:max-w-[800px] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <FilePlus className="w-5 h-5 text-gray-700" />
            Create New Lot
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-3 flex gap-3 text-sm text-blue-800">
            <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
            <p>
              Start a new lot for a payment centre. The system blocks a second
              open lot for the same centre + date to prevent accidental
              duplicates.
            </p>
          </div>

          <div className="mt-4">
            <DynamicForm
              form={form}
              fields={fields}
              onSubmit={handleSubmit}
              gridColumns={2}
              responsiveColumns={{ sm: 1, md: 2, lg: 2 }}
              onReset={handleClose}
              loading={isPending}
              config={{
                layout: "vertical",
                showSubmitButton: true,
                submitButtonText: "Create Lot",
                icon: Check,
                showResetButton: true,
                resetButtonText: "Cancel",
                classes: BUTTON_FIELD_CLASSES,
              }}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSlipLotDialog;
